import { toast } from './ui.js';

export function getActiveTable() {
  // If detail modal is open and has a table, use that
  const modalRoot = document.getElementById('modalRoot');
  if (modalRoot && modalRoot.innerHTML.trim() !== '') {
    const table = modalRoot.querySelector('table');
    if (table) {
      return { 
        table, 
        title: modalRoot.querySelector('h2')?.textContent || 'Export' 
      };
    }
  }
  // Otherwise, look for table in main content
  const content = document.getElementById('content');
  if (content) {
    const table = content.querySelector('table');
    if (table) {
      return { 
        table, 
        title: document.querySelector('.page-title')?.textContent || content.querySelector('h2')?.textContent || 'Export' 
      };
    }
  }
  return null;
}

export function handlePrint() {
  window.print();
}

export function handleExport() {
  const active = getActiveTable();
  if (!active) {
    toast('No table found to export');
    return;
  }
  const { table, title } = active;
  const rows = Array.from(table.querySelectorAll('tr'));
  
  const csvLines = [];
  for (const row of rows) {
    const cols = Array.from(row.querySelectorAll('th, td'));
    const rowData = [];
    
    for (const col of cols) {
      // Skip actions column / delete buttons
      if (col.tagName === 'TH' && (col.textContent.trim().toLowerCase() === 'actions' || col.textContent.trim().toLowerCase() === 'action')) {
        continue;
      }
      if (col.querySelector('button.del')) {
        continue;
      }
      
      let text = col.innerText || col.textContent || '';
      const chev = col.querySelector('.chev');
      if (chev) {
        const chevText = chev.innerText || chev.textContent || '';
        text = text.replace(chevText, '');
      }
      
      text = text.trim().replace(/\s+/g, ' ');
      // Escape quotes
      text = text.replace(/"/g, '""');
      rowData.push(`"${text}"`);
    }
    
    if (rowData.length > 0) {
      csvLines.push(rowData.join(','));
    }
  }
  
  if (csvLines.length <= 1) {
    toast('No data available to export');
    return;
  }
  
  const csvContent = csvLines.join('\r\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const filename = `${title.toLowerCase().replace(/[^a-z0-9]+/g, '_')}_export.csv`;
  
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  
  toast('CSV Exported');
}

async function loadJsPDF() {
  if (window.jspdf) return window.jspdf;
  
  toast('Loading PDF Generator…');
  
  await new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
  
  await new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.5.29/jspdf.plugin.autotable.min.js';
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
  
  return window.jspdf;
}

export async function handlePDF() {
  const active = getActiveTable();
  if (!active) {
    toast('No table found to export');
    return;
  }
  const { table, title } = active;
  
  try {
    const jspdfModule = await loadJsPDF();
    const { jsPDF } = jspdfModule;
    
    // Extract data
    const rows = Array.from(table.querySelectorAll('tr'));
    let headers = [];
    const body = [];
    
    for (const row of rows) {
      const isHeader = row.querySelector('th');
      const cols = Array.from(row.querySelectorAll('th, td'));
      const rowData = [];
      
      for (const col of cols) {
        // Skip actions column
        if (col.tagName === 'TH' && (col.textContent.trim().toLowerCase() === 'actions' || col.textContent.trim().toLowerCase() === 'action')) {
          continue;
        }
        if (col.querySelector('button.del')) {
          continue;
        }
        
        let text = col.innerText || col.textContent || '';
        const chev = col.querySelector('.chev');
        if (chev) {
          const chevText = chev.innerText || chev.textContent || '';
          text = text.replace(chevText, '');
        }
        
        text = text.trim().replace(/\s+/g, ' ');
        rowData.push(text);
      }
      
      if (rowData.length > 0) {
        if (isHeader) {
          headers = rowData;
        } else {
          body.push(rowData);
        }
      }
    }
    
    if (body.length === 0) {
      toast('No data available to export');
      return;
    }
    
    const doc = new jsPDF();
    
    // Add title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(title, 14, 15);
    
    // Add subtitle / date
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated on ${new Date().toLocaleString()} | SSREC ERP`, 14, 22);
    
    // Add table using autoTable
    doc.autoTable({
      head: [headers],
      body: body,
      startY: 28,
      theme: 'striped',
      headStyles: { fillColor: [22, 49, 61], textColor: [255, 255, 255] }, // Match --navy color
      styles: { font: "helvetica", fontSize: 10, cellPadding: 4 },
      alternateRowStyles: { fillColor: [248, 246, 240] } // Match --surface / lighter tone
    });
    
    const filename = `${title.toLowerCase().replace(/[^a-z0-9]+/g, '_')}_export.pdf`;
    doc.save(filename);
    toast('PDF Exported');
  } catch (error) {
    console.error('Failed to export PDF:', error);
    toast('PDF generation failed. Try printing.');
  }
}
